import { useState } from "react";
import { Plus, Trash2, RefreshCw, TrendingUp } from "lucide-react";
import { formatAmount } from "@/utils/formatAmount";
import { Spinner } from "@/components/ui/Spinner";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  useCurrencyAccounts,
  useCreateCurrencyAccount,
  useUpdateCurrencyAccount,
  useDeleteCurrencyAccount,
  useExchangeRates,
  useCompanies,
} from "@/features/group_finance/hooks/useGroupFinance";
import type { ForeignCurrencyAccount } from "@/features/group_finance/api/groupFinance";

const CURRENCIES = ["EUR", "USD", "GBP", "CHF", "CZK", "NOK", "SEK", "DKK"] as const;
type Currency = (typeof CURRENCIES)[number];

function fmtFx(val: string | null | undefined, currency: string) {
  if (!val) return "—";
  return formatAmount(val) + ` ${currency}`;
}
function fmtPln(val: string | null | undefined) {
  if (!val) return "—";
  return formatAmount(val) + " PLN";
}
function fmtDate(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("pl-PL");
}

type FormState = {
  company: string;
  currency: Currency;
  name: string;
  iban: string;
  balance: string;
  balance_date: string;
  notes: string;
};

const emptyForm = (): FormState => ({
  company: "",
  currency: "EUR",
  name: "",
  iban: "",
  balance: "0",
  balance_date: new Date().toISOString().slice(0, 10),
  notes: "",
});

interface Props {
  isCeo: boolean;
}

export function CurrencyAccountsPanel({ isCeo }: Props) {
  const { data: accounts = [], isLoading } = useCurrencyAccounts();
  const { data: rates = [] } = useExchangeRates();
  const { data: companies = [] } = useCompanies();
  const { mutate: createAccount, isPending: creating } = useCreateCurrencyAccount();
  const { mutate: updateAccount } = useUpdateCurrencyAccount();
  const { mutate: deleteAccount } = useDeleteCurrencyAccount();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [editBalance, setEditBalance] = useState<{ id: string; balance: string; balance_date: string } | null>(null);

  // Grouped by company
  const byCompany = companies
    .filter((c) => accounts.some((a) => a.company === c.id))
    .map((c) => ({
      company: c,
      accounts: accounts.filter((a) => a.company === c.id),
    }));

  // PLN totals
  const totalPln = accounts.reduce((sum, a) => {
    if (!a.balance_pln) return sum;
    return sum + parseFloat(a.balance_pln);
  }, 0);

  const handleCreate = () => {
    if (!form.company || !form.name || !form.balance_date) return;
    createAccount(
      {
        company: form.company,
        currency: form.currency,
        name: form.name,
        iban: form.iban,
        balance: form.balance,
        balance_date: form.balance_date,
        notes: form.notes,
      },
      {
        onSuccess: () => {
          setForm(emptyForm());
          setShowForm(false);
        },
      },
    );
  };

  const handleSaveBalance = () => {
    if (!editBalance) return;
    updateAccount(
      { id: editBalance.id, data: { balance: editBalance.balance, balance_date: editBalance.balance_date } },
      { onSuccess: () => setEditBalance(null) },
    );
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-6">
      {/* Header strip */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)] uppercase tracking-wider">
            Konta walutowe
          </h3>
          {accounts.length > 0 && (
            <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
              Łącznie ≈{" "}
              <span className="font-semibold text-[var(--color-text-primary)]">
                {formatAmount(totalPln.toFixed(2))} PLN
              </span>{" "}
              wg kursów NBP
            </p>
          )}
        </div>
        {isCeo && (
          <button
            onClick={() => setShowForm((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded bg-[var(--color-primary)] text-white hover:opacity-90 transition-opacity"
          >
            <Plus size={13} />
            Dodaj konto
          </button>
        )}
      </div>

      {/* Add form */}
      {showForm && isCeo && (
        <div className="card p-4 border border-[var(--color-border)] space-y-3 bg-[var(--color-bg-surface)]">
          <p className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
            Nowe konto walutowe
          </p>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <label className="block text-[var(--color-text-muted)] mb-1">Spółka *</label>
              <select
                value={form.company}
                onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                className="w-full border border-[var(--color-border)] rounded px-2 py-1.5 bg-[var(--color-bg-input)]"
              >
                <option value="">— wybierz —</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.short_code}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[var(--color-text-muted)] mb-1">Waluta *</label>
              <select
                value={form.currency}
                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value as Currency }))}
                className="w-full border border-[var(--color-border)] rounded px-2 py-1.5 bg-[var(--color-bg-input)]"
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-[var(--color-text-muted)] mb-1">Nazwa konta *</label>
              <input
                type="text"
                placeholder="np. Konto EUR ING"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full border border-[var(--color-border)] rounded px-2 py-1.5 bg-[var(--color-bg-input)]"
              />
            </div>
            <div>
              <label className="block text-[var(--color-text-muted)] mb-1">IBAN</label>
              <input
                type="text"
                placeholder="PL00 0000 0000 …"
                value={form.iban}
                onChange={(e) => setForm((f) => ({ ...f, iban: e.target.value }))}
                className="w-full border border-[var(--color-border)] rounded px-2 py-1.5 bg-[var(--color-bg-input)] font-mono"
              />
            </div>
            <div>
              <label className="block text-[var(--color-text-muted)] mb-1">Saldo *</label>
              <input
                type="number"
                step="0.01"
                value={form.balance}
                onChange={(e) => setForm((f) => ({ ...f, balance: e.target.value }))}
                className="w-full border border-[var(--color-border)] rounded px-2 py-1.5 bg-[var(--color-bg-input)]"
              />
            </div>
            <div>
              <label className="block text-[var(--color-text-muted)] mb-1">Data salda *</label>
              <DatePicker
                value={form.balance_date}
                onChange={(v) => setForm((f) => ({ ...f, balance_date: v }))}
              />
            </div>
            <div>
              <label className="block text-[var(--color-text-muted)] mb-1">Notatki</label>
              <input
                type="text"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="w-full border border-[var(--color-border)] rounded px-2 py-1.5 bg-[var(--color-bg-input)]"
              />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={handleCreate}
              disabled={creating || !form.company || !form.name}
              className="text-xs px-3 py-1.5 bg-[var(--color-primary)] text-white rounded hover:opacity-90 disabled:opacity-50"
            >
              {creating ? "Zapisywanie…" : "Zapisz"}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="text-xs px-3 py-1.5 border border-[var(--color-border)] rounded hover:bg-[var(--color-bg-hover)]"
            >
              Anuluj
            </button>
          </div>
        </div>
      )}

      {/* Rate strip */}
      {rates.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {rates.map((r) => (
            <div
              key={r.currency}
              className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded bg-[var(--color-bg-subtle)] border border-[var(--color-border)] text-[var(--color-text-secondary)]"
              title={`NBP ${r.table_no} · ${fmtDate(r.date)}`}
            >
              <TrendingUp size={10} className="text-emerald-500" />
              1 {r.currency} = {formatAmount(r.rate)} PLN
              <span className="text-[var(--color-text-muted)]">({fmtDate(r.date)})</span>
            </div>
          ))}
        </div>
      )}

      {/* Accounts grouped by company */}
      {accounts.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)] italic">Brak kont walutowych.</p>
      ) : (
        <div className="space-y-4">
          {byCompany.map(({ company, accounts: cAccounts }) => {
            const companyPln = cAccounts.reduce(
              (s, a) => s + parseFloat(a.balance_pln ?? "0"),
              0,
            );
            return (
              <div key={company.id}>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-xs font-semibold text-[var(--color-text-primary)] uppercase tracking-wide">
                    {company.short_code}
                  </span>
                  <span className="text-[11px] text-[var(--color-text-muted)]">
                    ≈ {formatAmount(companyPln.toFixed(2))} PLN
                  </span>
                </div>
                <div className="space-y-1.5">
                  {cAccounts.map((acc) => (
                    <AccountRow
                      key={acc.id}
                      account={acc}
                      isCeo={isCeo}
                      editBalance={editBalance}
                      setEditBalance={setEditBalance}
                      onSaveBalance={handleSaveBalance}
                      onDelete={() => deleteAccount(acc.id)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AccountRow({
  account,
  isCeo,
  editBalance,
  setEditBalance,
  onSaveBalance,
  onDelete,
}: {
  account: ForeignCurrencyAccount;
  isCeo: boolean;
  editBalance: { id: string; balance: string; balance_date: string } | null;
  setEditBalance: (v: { id: string; balance: string; balance_date: string } | null) => void;
  onSaveBalance: () => void;
  onDelete: () => void;
}) {
  const isEditingThis = editBalance?.id === account.id;

  return (
    <div className="card flex items-center gap-3 px-3 py-2.5 text-xs bg-[var(--color-bg-surface)] border border-[var(--color-border)]">
      {/* Currency badge */}
      <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-[var(--color-bg-subtle)] font-bold text-[var(--color-text-primary)] text-[11px] shrink-0">
        {account.currency}
      </span>

      {/* Name + IBAN */}
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-[var(--color-text-primary)] truncate">{account.name}</p>
        {account.iban && (
          <p className="text-[var(--color-text-muted)] font-mono truncate">{account.iban}</p>
        )}
      </div>

      {/* Balance */}
      {isEditingThis ? (
        <div className="flex items-center gap-1.5 shrink-0">
          <input
            type="number"
            step="0.01"
            value={editBalance!.balance}
            onChange={(e) => setEditBalance({ ...editBalance!, balance: e.target.value })}
            className="w-28 border border-[var(--color-border)] rounded px-1.5 py-1 bg-[var(--color-bg-input)] text-right"
            autoFocus
          />
          <DatePicker
            value={editBalance!.balance_date}
            onChange={(v) => setEditBalance({ ...editBalance!, balance_date: v })}
          />
          <button
            onClick={onSaveBalance}
            className="px-2 py-1 bg-[var(--color-primary)] text-white rounded hover:opacity-90 text-[11px]"
          >
            OK
          </button>
          <button
            onClick={() => setEditBalance(null)}
            className="px-2 py-1 border border-[var(--color-border)] rounded hover:bg-[var(--color-bg-hover)] text-[11px]"
          >
            ✕
          </button>
        </div>
      ) : (
        <div className="text-right shrink-0">
          <p className="font-semibold text-[var(--color-text-primary)]">
            {fmtFx(account.balance, account.currency)}
          </p>
          {account.balance_pln && (
            <p className="text-[var(--color-text-muted)] text-[11px]">
              ≈ {fmtPln(account.balance_pln)}
            </p>
          )}
          <p className="text-[var(--color-text-muted)] text-[10px]">
            stan: {fmtDate(account.balance_date)}
          </p>
          {account.rate && (
            <p className="text-[var(--color-text-muted)] text-[10px]">
              kurs: {formatAmount(account.rate)} PLN ({fmtDate(account.rate_date)})
            </p>
          )}
        </div>
      )}

      {/* Actions */}
      {isCeo && !isEditingThis && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            title="Aktualizuj saldo"
            onClick={() =>
              setEditBalance({
                id: account.id,
                balance: account.balance,
                balance_date: new Date().toISOString().slice(0, 10),
              })
            }
            className="p-1.5 rounded hover:bg-[var(--color-bg-hover)] text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
          >
            <RefreshCw size={13} />
          </button>
          <button
            title="Usuń konto"
            onClick={onDelete}
            className="p-1.5 rounded hover:bg-red-50 text-[var(--color-text-muted)] hover:text-red-600"
          >
            <Trash2 size={13} />
          </button>
        </div>
      )}
    </div>
  );
}
